/**
 * components/businessRegionSelect.tsx
 *
 * BUSINESS REGION — the one question on the signup form that decides where the
 * organisation's data physically lives, and the only answer on it that can
 * never be changed afterwards.
 *
 * ── WHY THIS IS NOT CountrySelect ─────────────────────────────────────────
 *
 * CountrySelect offers ~200 countries and sets a commercial fact (tax pack +
 * tax profile). This offers TWO, because two is how many jurisdictions SK
 * actually runs identity in — `ae` (me-central-1) and `in` (asia-south1).
 * Listing a country we cannot host in would be a promise the platform can't
 * keep, and unlike a wrong tax country, a wrong residency zone is not
 * correctable later: fixing it means relocating every customer identity record
 * across a jurisdictional boundary. That is a migration, not a setting.
 *
 * The backend uses this one answer to fill two INDEPENDENT columns: the org's
 * immutable residency `zone`, and an initial `country` (its legal/tax home,
 * which an org_admin can change later — nothing recomputes it from the zone).
 * So the admin answers ONE question instead of the same question twice in two
 * vocabularies. Asking twice is how the two facts got conflated before — a UAE
 * operator whose phone flag said India was registered as being in India.
 *
 * Note what this control does NOT claim: a CURRENCY. Money is denominated per
 * DOCUMENT in sk-billing — the same org may invoice one customer in AED and
 * another in INR — so a currency implied by a region would be a promise the
 * billing model does not make.
 *
 * ── WHERE THE CONSEQUENCE IS SHOWN ────────────────────────────────────────
 *
 * In the DROPDOWN ROWS, not in a panel under the field. Every option names its
 * own city and privacy law ("Data in Dubai · UAE PDPL"), so the consequence is
 * in front of the reader at the moment they choose — which is the moment it
 * matters, since the value cannot be changed afterwards.
 *
 * A standing summary panel under the picker was tried and removed (it made the
 * form long enough to bury the fields after it). If a reviewer later asks for
 * the immutability warning back on the form itself, the honest place for it is
 * one line of muted text under the trigger — NOT a tooltip, because a
 * consequence a reader has to hover to discover is one most readers never see.
 *
 * Note on the default: `ae` arrives pre-selected (product decision — Dubai is
 * the primary market). That is a deliberate departure from the provisioning
 * console's no-default rule for immutable fields.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
  Animated,
  Easing,
  Dimensions,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import OurIcon from './icon';
import RequiredMark from './requiredMark';
import { useTheme } from '../context/themeContext';

/** The two deployed regions. Mirrors the API's REGION_FACTS — same codes, same
 *  facts — so what the admin reads here is what the org is provisioned with. */
export interface BusinessRegion {
  /** Sent to the API as `region`. Also the org's residency zone code. */
  readonly code: 'ae' | 'in';
  readonly flag: string;
  /** What the admin recognises. */
  readonly name: string;
  /** The city they'd actually name if asked where their data is. */
  readonly city: string;
  /** The privacy law that governs it. */
  readonly regime: string;
  /**
   * ISO-3166-1 alpha-2 the API derives from this region. Not shown in the
   * picker — it is here because the signup screen reads it to look up the
   * matching phone entry, and because it documents what the region implies.
   */
  readonly country: string;
}

export const BUSINESS_REGIONS: BusinessRegion[] = [
  {
    code: 'ae',
    flag: '🇦🇪',
    name: 'United Arab Emirates',
    city: 'Dubai',
    regime: 'UAE PDPL',
    country: 'AE',
  },
  {
    code: 'in',
    flag: '🇮🇳',
    name: 'India',
    city: 'Mumbai',
    regime: 'DPDP Act 2023',
    country: 'IN',
  },
];

/** Dubai. The primary market, so it needs no extra tap in the common case. */
export const DEFAULT_BUSINESS_REGION: BusinessRegion = BUSINESS_REGIONS[0];

export interface BusinessRegionSelectProps {
  readonly value: BusinessRegion;
  readonly onChange: (region: BusinessRegion) => void;
  readonly label?: string;
  readonly required?: boolean;
  readonly editable?: boolean;
  readonly error?: string;
  /** Override the field's outer spacing (e.g. a denser form). */
  readonly containerStyle?: StyleProp<ViewStyle>;
}

const DROPDOWN_MIN_WIDTH = 280;

const BusinessRegionSelect: React.FC<BusinessRegionSelectProps> = ({
  value,
  onChange,
  label = 'BUSINESS REGION',
  required = false,
  editable = true,
  error,
  containerStyle,
}) => {
  const { theme } = useTheme();
  const C = theme.colors;

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(
    null,
  );
  const rowRef = useRef<View>(null);

  // A ring pulses around the picker when the region changes, so the change is
  // acknowledged at the point of interaction. Skipped on first render — an
  // animation that fires on mount reads as a glitch, not as feedback.
  const pulse = useRef(new Animated.Value(0)).current;
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    pulse.setValue(0);
    Animated.sequence([
      Animated.timing(pulse, {
        toValue: 1,
        duration: 180,
        easing: Easing.out(Easing.quad),
        useNativeDriver: false,
      }),
      Animated.timing(pulse, {
        toValue: 0,
        duration: 420,
        easing: Easing.in(Easing.quad),
        useNativeDriver: false,
      }),
    ]).start();
  }, [value.code, pulse]);

  const openPicker = () => {
    if (!editable) return;
    rowRef.current?.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
      setOpen(true);
    });
  };

  const screen = Dimensions.get('window');
  const width = anchor
    ? Math.min(screen.width - 16, Math.max(DROPDOWN_MIN_WIDTH, anchor.width))
    : DROPDOWN_MIN_WIDTH;
  const left = anchor ? Math.max(8, Math.min(anchor.x, screen.width - width - 8)) : 8;
  // Two rows plus their subtitles — a fixed, small panel, so it only needs to
  // flip upward when there is genuinely no room below.
  const PANEL_HEIGHT = 168;
  const spaceBelow = anchor ? screen.height - (anchor.y + anchor.height) - 12 : 0;
  const openUp = spaceBelow < PANEL_HEIGHT && anchor ? anchor.y - 12 > spaceBelow : false;
  const position = anchor
    ? openUp
      ? { bottom: screen.height - anchor.y + 6 }
      : { top: anchor.y + anchor.height + 6 }
    : {};

  const ringColor = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(0,0,0,0)', `${C.accent}66`],
  });

  return (
    <View style={[styles.wrap, containerStyle]}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, { color: C.ink }]}>{label}</Text>
        {required ? <RequiredMark /> : null}
        <Text style={[styles.hint, { color: C.ink3 }]} numberOfLines={1}>
          (where your data is stored)
        </Text>
      </View>

      {/* Trigger — same box language as the country field so the form reads as
          one set of controls rather than a special case bolted on. */}
      <Animated.View style={[styles.triggerRing, { shadowColor: C.accent, borderColor: ringColor }]}>
        <TouchableOpacity
          ref={rowRef}
          activeOpacity={0.8}
          onPress={openPicker}
          disabled={!editable}
          accessibilityRole="button"
          accessibilityLabel={`Business region: ${value.name}. Sets where your data is stored.`}
          style={[
            styles.box,
            !editable && { opacity: 0.6 },
            { backgroundColor: C.card, borderColor: error ? C.danger : open ? C.accent : C.line },
          ]}
        >
          <Text style={styles.flag}>{value.flag}</Text>
          <View style={styles.triggerText}>
            <Text style={[styles.name, { color: C.ink }]} numberOfLines={1}>
              {value.name}
            </Text>
            <Text style={[styles.nameSub, { color: C.ink3 }]} numberOfLines={1}>
              {value.city}
            </Text>
          </View>
          <OurIcon name="chev-d" size={16} color={C.ink3} />
        </TouchableOpacity>
      </Animated.View>

      {error ? <Text style={[styles.error, { color: C.danger }]}>{error}</Text> : null}

      {/* ── Picker ─────────────────────────────────────────────────────── */}
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} />
        <View
          style={[
            styles.dropdown,
            position,
            { left, width, borderColor: C.line, backgroundColor: C.card },
          ]}
        >
          {BUSINESS_REGIONS.map((r, i) => {
            const selected = r.code === value.code;
            return (
              <TouchableOpacity
                key={r.code}
                activeOpacity={0.7}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                onPress={() => {
                  onChange(r);
                  setOpen(false);
                }}
                style={[
                  styles.option,
                  i > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: C.line },
                  selected && { backgroundColor: C.accentSoft },
                ]}
              >
                <Text style={styles.rowFlag}>{r.flag}</Text>
                <View style={styles.optionText}>
                  <Text style={[styles.rowName, { color: C.ink }]} numberOfLines={1}>
                    {r.name}
                  </Text>
                  {/* Each row carries its OWN consequence, so the choice is
                      informed at the moment it is made — not only after. */}
                  <Text style={[styles.rowSub, { color: C.ink3 }]} numberOfLines={1}>
                    Data in {r.city} · {r.regime}
                  </Text>
                </View>
                {selected ? <OurIcon name="check" size={15} color={C.accent} /> : null}
              </TouchableOpacity>
            );
          })}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  // Owns its own trailing gap, matching CommonInput's 18 so this field sits in
  // the same vertical rhythm as the rest of the form.
  wrap: { width: '100%', marginBottom: 18 },
  labelRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, paddingHorizontal: 4 },
  label: { fontSize: 12, fontWeight: '800', letterSpacing: 1.5, textTransform: 'uppercase' },
  hint: {
    flex: 1,
    fontSize: 11,
    fontWeight: '500',
    marginLeft: 6,
    letterSpacing: 0,
    textTransform: 'none',
  },
  triggerRing: {
    borderRadius: 4,
    borderWidth: 2,
    // Animated between transparent and an accent tint on change.
    borderColor: 'rgba(0,0,0,0)',
  },
  box: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 46,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1.5,
    borderRadius: 2,
    ...(Platform.OS === 'web'
      ? ({ cursor: 'pointer', transition: 'border-color 160ms ease' } as any)
      : {}),
  },
  triggerText: { flex: 1 },
  flag: { fontSize: 20 },
  name: { fontSize: 14, fontWeight: '700' },
  nameSub: { fontSize: 11, fontWeight: '500', marginTop: 1 },
  error: { fontSize: 11, marginTop: 4, paddingHorizontal: 4 },


  dropdown: {
    position: 'absolute',
    borderWidth: 1,
    borderRadius: 6,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 12,
  },
  option: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 14, paddingVertical: 12 },
  optionText: { flex: 1 },
  rowFlag: { fontSize: 20 },
  rowName: { fontSize: 13.5, fontWeight: '700' },
  rowSub: { fontSize: 11, fontWeight: '500', marginTop: 2 },
});

export default BusinessRegionSelect;
